import React, { useState, useEffect } from "react";
import { IonButton, IonSpinner } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useRequestCallWithSmsFallback } from "../utils/useRequestCallWithSmsFallback";
import { VerificationMethod } from "../types";

interface ResendCallCodeProps {
  phone: string;
  onSendSuccess?: (data: { verificationCodeId: string; phoneNumber: string; method: VerificationMethod }) => void;
  onSendError?: (error: unknown) => void;
  initialTimer?: number;
  className?: string;
}

export default function ResendCallCode({
  phone,
  onSendSuccess,
  onSendError,
  initialTimer = 15,
  className = "text-black flex",
}: ResendCallCodeProps) {
  const { t } = useTranslation();
  const { requestVerification, isPending: isCallQueryPending } = useRequestCallWithSmsFallback();
  const [timer, setTimer] = useState(initialTimer);

  const isTimerActive = timer > 0;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerActive) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerActive]);

  const handleResendCall = () => {
    requestVerification(phone, {
      onSuccess: (data, method) => {
        setTimer(initialTimer);
        onSendSuccess?.({
          verificationCodeId: data.verificationCodeId,
          phoneNumber: data.phoneNumber,
          method,
        });
      },
      onError: (error) => {
        console.error("Failed to make call:", error);
        onSendError?.(error);
      },
    });
  };

  const isDisabled = isTimerActive || isCallQueryPending;

  return (
    <IonButton fill="clear" className={className} onClick={handleResendCall} disabled={isDisabled}>
      <span>
        {isTimerActive ? `${t("Call again")} ( ${timer} )` : isCallQueryPending ? t("Calling...") : t("Call again")}
      </span>
      {isCallQueryPending && <IonSpinner className="ml-2 size-5 text-red-600" />}
    </IonButton>
  );
}
